/**
 * frontend/src/components/JackpotOverlay.jsx
 * SoundDAO Sprint 4 finale — full-screen Big Break Bonus celebration overlay.
 *
 * Shown when a login reward returns tier === 'big_break' (1,000 SOUND).
 * Plays the jackpot sound on mount, shows share prompts, and dismisses
 * via "Maybe later" or the onDismiss callback.
 *
 * Props:
 *   amount       — number, default 1000
 *   referralCode — string | null, user's referral code for share copy
 *   onDismiss    — () => void
 */

import React, { useEffect, useState } from 'react';
import { playRewardSound } from '../utils/rewardSounds';
import './JackpotOverlay.css';
import { inviteUrl } from '../lib/constants';

export default function JackpotOverlay({ amount = 1000, referralCode, onDismiss }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    playRewardSound('jackpot');
  }, []);

  const link = inviteUrl(referralCode);   // https://app.sounddao.com/invite/<code>
  const shareText = `I just got my Big Break Bonus at Big Creative — ${amount.toLocaleString()} SOUND dropped in my account! This is my moment. Join me for yours: ${link}`;

  const handleShareX = () => {
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`,
      '_blank',
      'noopener'
    );
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(shareText)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        window.prompt('Copy your Big Break Bonus message:', shareText);
      });
  };

  return (
    <div className="jackpot-overlay" onClick={e => e.stopPropagation()}>
      <div className="jackpot-modal">

        {/* CSS-only sparkles */}
        <div className="jackpot-sparkles" aria-hidden="true">
          {[...Array(12)].map((_, i) => (
            <div key={i} className={`jackpot-spark jackpot-spark--${i}`} />
          ))}
        </div>

        {/* Glowing headphone circle */}
        <div className="jackpot-glow-ring">
          <span className="jackpot-emoji" role="img" aria-label="headphones">🎧</span>
        </div>

        {/* Big Break Bonus badge */}
        <div className="jackpot-badge">Big Break Bonus</div>

        {/* Headline */}
        <h2 className="jackpot-headline">Your Big Break Bonus</h2>

        {/* Amount */}
        <div className="jackpot-amount">
          <span className="jackpot-amount-number">{amount.toLocaleString()}</span>
          <span className="jackpot-amount-unit">SOUND</span>
        </div>

        {/* Subtext */}
        <p className="jackpot-subtext">
          The hustle paid off. This one's yours.
        </p>

        {/* Share bubble */}
        <div className="jackpot-share-bubble">
          <p className="jackpot-share-text">{shareText}</p>
        </div>

        {/* Action buttons */}
        <div className="jackpot-actions">
          <button className="jackpot-btn jackpot-btn--x" onClick={handleShareX}>
            {/* X (Twitter) logo */}
            <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.741l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
            Share on X
          </button>
          <button className="jackpot-btn jackpot-btn--copy" onClick={handleCopy}>
            {copied ? '✓ Copied!' : 'Copy'}
          </button>
        </div>

        {/* Dismiss */}
        <button className="jackpot-dismiss" onClick={onDismiss}>
          Maybe later
        </button>
      </div>
    </div>
  );
}
